import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatCurrency, formatCurrencyDynamic } from '../../../utils/numberUtils';
import './DeviceQuickView.css';
import { API_ENDPOINTS } from '../../../config/apiConfig';

function DeviceQuickView({ deviceId, token, onClose, onViewFullDetails }) {
  const [device, setDevice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (deviceId) {
      fetchDevice();
    }
  }, [deviceId]);

  const fetchDevice = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await axios.get(API_ENDPOINTS.DEVICES.GET(deviceId), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setDevice(response.data);
    } catch (err) {
      console.error('Error fetching device:', err);
      setError('Failed to load device details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getWarrantyStatus = (device) => {
    if (!device.warranty_expiry) return 'Not set';
    const expiryDate = new Date(device.warranty_expiry);
    const today = new Date();
    if (expiryDate < today) {
      const daysExpired = Math.floor((today - expiryDate) / (1000 * 60 * 60 * 24));
      return `Expired (${daysExpired} days ago)`;
    }
    const daysLeft = Math.floor((expiryDate - today) / (1000 * 60 * 60 * 24));
    return `Active (${daysLeft} days remaining)`;
  };

  const getMaintenanceStatus = (device) => {
    const nextDate = device.ppm_next_due_date;
    if (!nextDate) return 'Not scheduled';
    const dueDate = new Date(nextDate);
    const today = new Date();
    if (dueDate < today) {
      const daysOverdue = Math.floor((today - dueDate) / (1000 * 60 * 60 * 24));
      return `Overdue (${daysOverdue} days)`;
    }
    const daysLeft = Math.floor((dueDate - today) / (1000 * 60 * 60 * 24));
    return `Due in ${daysLeft} days`;
  };

  if (loading) {
    return <div className="device-quick-view-loading">Loading device details...</div>;
  }

  if (error || !device) {
    return <div className="device-quick-view-error">{error || 'Device not found'}</div>;
  }

  return (
    <div className="device-quick-view-container">
      {/* Header with device name */}
      <div className="quick-view-header">
        <div>
          <h3>{device.name}</h3>
          <p className="device-serial"><code>{device.serial_number}</code></p>
        </div>
        <span className="department-badge">{device.department}</span>
      </div>

      {/* Essential Details Grid */}
      <div className="quick-view-content">
        {/* Basic Info */}
        <div className="quick-info-section">
          <div className="section-title">Basic Information</div>
          <div className="quick-info-grid">
            <div className="quick-info-item">
              <span className="label">Model</span>
              <span className="value">{device.model}</span>
            </div>
            <div className="quick-info-item">
              <span className="label">Manufacturer</span>
              <span className="value">{device.manufacturer}</span>
            </div>
            <div className="quick-info-item">
              <span className="label">Physical Location</span>
              <span className="value">{device.physical_location}</span>
            </div>
            <div className="quick-info-item">
              <span className="label">Device Cost</span>
              <span className="value">{formatCurrency(device.product_cost)} <strong>({formatCurrencyDynamic(device.product_cost)})</strong></span>
            </div>
          </div>
        </div>

        {/* Maintenance & Warranty Status */}
        <div className="quick-info-section">
          <div className="section-title">Status</div>
          <div className="quick-status-grid">
            <div className="status-item">
              <span className="status-label">Warranty Status</span>
              <span className={`status-value ${getWarrantyStatus(device).includes('Expired') ? 'warning' : 'success'}`}>
                {getWarrantyStatus(device)}
              </span>
            </div>
            <div className="status-item">
              <span className="status-label">Maintenance Status</span>
              <span className={`status-value ${getMaintenanceStatus(device).includes('Overdue') ? 'critical' : 'info'}`}>
                {getMaintenanceStatus(device)}
              </span>
            </div>
            {device.ppm_last_done_date && (
              <div className="status-item">
                <span className="status-label">Last Maintenance</span>
                <span className="status-value">{formatDate(device.ppm_last_done_date)}</span>
              </div>
            )}
            <div className="status-item">
              <span className="status-label">Service Contract</span>
              <span className="status-value">{device.service_contract_type || 'Not set'}</span>
            </div>
          </div>
        </div>

        {/* Action Link */}
        <div className="quick-view-action">
          <button
            className="view-full-details-link"
            onClick={() => onViewFullDetails(deviceId)}
          >
            📋 View Full Device Details →
          </button>
        </div>
      </div>
    </div>
  );
}

export default DeviceQuickView;
